import { lazy, Suspense, type ComponentType, type ReactNode } from "react"
import {
  createBrowserRouter,
  Link,
  Navigate,
  RouterProvider,
  useLocation,
  useRouteError,
} from "react-router-dom"
import { useClerk } from "@clerk/react"
import { Loader2 } from "lucide-react"

import { ClientLoginPage } from "@/pages/ClientLoginPage"

type ClientAreaModule = typeof import("@/pages/ClientAreaPage")

const loadClientArea = () => import("@/pages/ClientAreaPage")

/**
 * A área do cliente inteira vive em um chunk separado, carregado só depois do
 * login. A página de login continua no bundle inicial, que fica pequeno.
 */
function clientAreaPage<K extends keyof ClientAreaModule>(key: K) {
  return lazy(async () => {
    const module = await loadClientArea()
    return { default: module[key] as ComponentType }
  })
}

const ClientAreaGuard = clientAreaPage("ClientAreaGuard")
const ClientInvitePage = clientAreaPage("ClientInvitePage")
const ClientDashboardPage = clientAreaPage("ClientDashboardPage")
const ClientOrganizationsPage = clientAreaPage("ClientOrganizationsPage")
const ClientRegistryPage = clientAreaPage("ClientRegistryPage")
const ClientTemplatesPage = clientAreaPage("ClientTemplatesPage")
const ClientCyclesPage = clientAreaPage("ClientCyclesPage")
const ClientCycleDetailPage = clientAreaPage("ClientCycleDetailPage")
const ClientDeliveriesPage = clientAreaPage("ClientDeliveriesPage")
const ClientCompetenciasPage = clientAreaPage("ClientCompetenciasPage")
const ClientPlanningPage = clientAreaPage("ClientPlanningPage")
const ClientMyEvaluationsPage = clientAreaPage("ClientMyEvaluationsPage")
const ClientEvaluationFormPage = clientAreaPage("ClientEvaluationFormPage")
const ClientReportsPage = clientAreaPage("ClientReportsPage")
const ClientUsersPage = clientAreaPage("ClientUsersPage")
// A página 404 reaproveita componentes do site institucional; fica fora do
// bundle inicial do login.
const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage }))
)

/**
 * O Suspense fica só em volta das rotas sob demanda. A rota /login não pode
 * ter um boundary acima dela: o app shell é hidratado e um Suspense exigiria
 * marcadores no HTML estático.
 */
function withSuspense(element: ReactNode) {
  return <Suspense fallback={<RouteLoadingScreen />}>{element}</Suspense>
}

function RouteLoadingScreen() {
  return (
    <div className="grid min-h-screen place-items-center bg-[#fbf9fd] text-[#433475]">
      <div className="flex items-center gap-2.5 rounded-full border border-[#e8e0ef] bg-white px-4 py-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando área do cliente
      </div>
    </div>
  )
}

function LegacyClientAreaRedirect() {
  const location = useLocation()
  const nextPath = location.pathname.replace(/^\/area-do-cliente/, "") || "/dashboard"

  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />
}

function ClientAreaRouteErrorPage() {
  const error = useRouteError()
  const { signOut } = useClerk()
  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : ""

  return (
    <main className="grid min-h-screen place-items-center bg-[#f7f4fb] px-4 py-10 text-[#201936]">
      <section className="w-full max-w-lg rounded-lg border border-[#ece6f3] bg-white p-6 shadow-[0_24px_80px_rgba(32,25,54,0.12)] sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#e82b77]">
          Quantum+
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">
          Não foi possível carregar esta tela.
        </h1>
        <p className="mt-3 text-sm leading-6 text-[#4a4060]">
          {error
            ? "A página encontrou uma inconsistência temporária nos dados. Atualize ou volte para o dashboard."
            : "Atualize ou volte para o dashboard para continuar."}
        </p>
        {import.meta.env.DEV && errorMessage ? (
          <p className="mt-3 rounded-md border border-rose-200/70 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">
            {errorMessage}
          </p>
        ) : null}
        <div className="mt-6 flex flex-wrap gap-2">
          <Link
            to="/dashboard"
            className="inline-flex h-10 items-center justify-center rounded-md bg-[#e82b77] px-4 text-sm font-semibold text-white transition hover:bg-[#cf1f66]"
          >
            Ir para o dashboard
          </Link>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex h-10 items-center justify-center rounded-md border border-[#ece6f3] bg-white px-4 text-sm font-semibold text-[#2d2342] transition hover:bg-[#f8f6fb]"
          >
            Atualizar
          </button>
          <button
            type="button"
            onClick={() => void signOut({ redirectUrl: "/login" })}
            className="inline-flex h-10 items-center justify-center rounded-md border border-[#ece6f3] bg-white px-4 text-sm font-semibold text-[#2d2342] transition hover:bg-[#f8f6fb]"
          >
            Sair
          </button>
        </div>
      </section>
    </main>
  )
}

const router = createBrowserRouter([
  { path: "/login", element: <ClientLoginPage /> },
  { path: "/convite/:token", element: withSuspense(<ClientInvitePage />) },
  { path: "/area-do-cliente/*", element: <LegacyClientAreaRedirect /> },
  {
    path: "/",
    element: withSuspense(<ClientAreaGuard />),
    errorElement: <ClientAreaRouteErrorPage />,
    children: [
      { index: true, element: <ClientDashboardPage /> },
      { path: "dashboard", element: <ClientDashboardPage /> },
      { path: "clientes", element: <ClientOrganizationsPage /> },
      { path: "cadastros", element: <ClientRegistryPage /> },
      { path: "colaboradores", element: <ClientRegistryPage /> },
      { path: "postos", element: <ClientRegistryPage /> },
      { path: "competencias", element: <ClientRegistryPage /> },
      { path: "templates", element: <ClientTemplatesPage /> },
      { path: "ciclos", element: <ClientCyclesPage /> },
      { path: "ciclos/:cycleId", element: <ClientCycleDetailPage /> },
      { path: "entregas", element: <ClientDeliveriesPage /> },
      { path: "competencias-por-avaliado", element: <ClientCompetenciasPage /> },
      { path: "planejamento", element: <ClientPlanningPage /> },
      { path: "minhas-avaliacoes", element: <ClientMyEvaluationsPage /> },
      { path: "avaliacoes/:assignmentId", element: <ClientEvaluationFormPage /> },
      { path: "relatorios", element: <ClientReportsPage /> },
      { path: "relatorios/:participantId", element: <ClientReportsPage /> },
      { path: "admin/usuarios", element: <ClientUsersPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
])

export default function QuantumMaisApp() {
  return <RouterProvider router={router} />
}
